import { AccountCircle } from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import {
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
  styled,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import * as React from "react";
import { Suspense } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { authLogout } from "../api/auth";
import { USER_TYPE } from "../const/enums";
import AuthorizationError from "../errors/AuthorizationError";
import { userAtom } from "../store/user";
import PasswordChange from "./Auth/PasswordChange";
import Footer from "./Footer";

const drawerWidth = 240;

const SidebarDiv = styled("div")(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  background: theme.palette.primary.main,
  height: "100vh",
}));

const SidebarListSubheader = styled(ListSubheader)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  background: theme.palette.primary.main,
}));

const CustomAppBar = styled(AppBar)(({ theme }) => ({
  color: theme.palette.common.black.main,
  background: theme.palette.common.white.main,
  height: "64px",
}));

function ResponsiveDrawer() {
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [passwordChangeOpen, setPasswordChangeOpen] = React.useState(false);
  const user = useRecoilValue(userAtom);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setPasswordChangeOpen(false);
  };

  const handleMenuLogout = () => {
    setAnchorEl(null);
    console.log("attempting logout..");
    return authLogout().then((data) => {
      localStorage.removeItem("access-token");
      navigate("/login");
    });
  };

  if (!user?.id) {
    throw new AuthorizationError("ログインしてください。");
  }

  const show_doctor = [USER_TYPE.DOCTOR, USER_TYPE.CHIEF].includes(
    user.user_type,
  );
  const show_chief = [USER_TYPE.CHIEF, USER_TYPE.ASSISTANT].includes(
    user.user_type,
  );
  const show_regional = user.user_type === USER_TYPE.REGIONAL;

  const drawer = (
    <SidebarDiv>
      <Toolbar />
      <Divider />
      {show_doctor && (
        <>
          <SidebarListSubheader>医師</SidebarListSubheader>
          <List>
            <ListItem key="医師トップ" disablePadding>
              <ListItemButton component={Link} to="/">
                <ListItemIcon>
                  <HomeIcon sx={{ color: "common.white.main" }} />
                </ListItemIcon>
                <ListItemText primary="トップ" />
              </ListItemButton>
            </ListItem>
            <ListItem key="当直予定" disablePadding>
              <ListItemButton component={Link} to="/on_duty_request">
                <ListItemIcon>
                  <CalendarTodayIcon sx={{ color: "common.white.main" }} />
                </ListItemIcon>
                <ListItemText primary="当直予定入力" />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}

      {show_chief && (
        <>
          <SidebarListSubheader>医局</SidebarListSubheader>
          <ListItem key="医局トップ" disablePadding>
            <ListItemButton component={Link} to="/chief_dashboard">
              <ListItemIcon>
                <HomeIcon sx={{ color: "common.white.main" }} />
              </ListItemIcon>
              <ListItemText primary="トップ" />
            </ListItemButton>
          </ListItem>
          <ListItem key="種別毎スケジュール" disablePadding>
            <ListItemButton component={Link} to="/chief_schedule1">
              <ListItemIcon>
                <CalendarTodayIcon sx={{ color: "common.white.main" }} />
              </ListItemIcon>
              <ListItemText primary="種別毎スケジュール" />
            </ListItemButton>
          </ListItem>
          <ListItem key="医師毎スケジュール" disablePadding>
            <ListItemButton component={Link} to="/chief_schedule2">
              <ListItemIcon>
                <CalendarTodayIcon sx={{ color: "common.white.main" }} />
              </ListItemIcon>
              <ListItemText primary="医師毎スケジュール" />
            </ListItemButton>
          </ListItem>
          <ListItem key="当直オンコール予定作成" disablePadding>
            <ListItemButton component={Link} to="/create_schedule">
              <ListItemIcon>
                <EditCalendarIcon sx={{ color: "common.white.main" }} />
              </ListItemIcon>
              <ListItemText primary="当直オンコール予定作成" />
            </ListItemButton>
          </ListItem>
        </>
      )}
      {show_regional && (
        <SidebarListSubheader>地域医療機関</SidebarListSubheader>
      )}
    </SidebarDiv>
  );

  return (
    <Box id="root" sx={{ display: "flex" }}>
      <CustomAppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Typography>{user.full_name}</Typography>
            <Box>
              <IconButton
                onClick={handleMenu}
                sx={{
                  color: "#BDBDBD",
                }}
              >
                <AccountCircle sx={{ fontSize: "40px" }} />
              </IconButton>
              <PasswordChange
                key={Date.now()}
                onClose={handleMenuClose}
                open={passwordChangeOpen}
              />
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => setPasswordChangeOpen(true)}
                  sx={{ fontSize: 14, color: "secondary.main" }}
                >
                  パスワード変更
                </MenuItem>
                <MenuItem
                  onClick={handleMenuLogout}
                  sx={{ fontSize: 14, color: "secondary.main" }}
                >
                  ログアウト
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </CustomAppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Suspense fallback={<div>Loading...</div>}>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          <Toolbar />
          <Outlet />
          <Footer />
        </Box>
      </Suspense>
    </Box>
  );
}

export default ResponsiveDrawer;
